import LoopControls from './components/LoopControls';
import SkillFilePanel from './components/SkillFilePanel';
import { KIND_LABEL, SUCC_COLOR } from './display';
import { agentType, runtimeTone } from './domain/agentStatus';
import { detailRunMeta } from './domain/liveData';
import { launchLabel } from './domain/plannedCard';
import type { LoopState } from '../../apps/apis/commandCenter/types';
import type { AgentCard, AgentDetail, DetailRow, DetailRunResult } from './types';

const RESULT: Record<DetailRunResult, [string, string]> = {
  ok: ['res-ok', '✓ success'],
  fail: ['res-fail', '✕ missed'],
  warn: ['res-fail', '! degraded'],
  idle: ['', '—'],
};

const FALLBACK: AgentDetail = {
  name: '',
  sub: '',
  dot: '',
  meta: { runs: '0', succ: ['new loop', 'idle'], metric: '—', next: '—', owner: '—' },
  rows: [['—', 'idle', 'no runs recorded yet', '—', '—']],
};

const PLANNED_ROWS: AgentDetail['rows'] = [['—', 'idle', 'planned idea — not started', '—', '—']];

interface AgentDetailViewProps {
  card: AgentCard;
  code: string | null;
  /** Override-aware values resolved by the page. */
  metricOverride: string | undefined;
  nextOverride: string | undefined;
  /** Live runs matched to this card; empty means fall back to the seeded rows. */
  liveRuns: DetailRow[];
  /** For n8n cards, the workflow editor URL; null for native agents (which show the Talk button). */
  n8nWorkflowUrl?: string | null;
  /** For Hermes cards, the Slack conversation with the owning agent; null when it has no bot. */
  slackAgentUrl?: string | null;
  /** The Hermes skill this card is named after; null for n8n cards and planned ideas. */
  skillId?: string | null;
  /** Present only for a managed Hermes or n8n monitoring card; all other details remain read-only. */
  onRemoveManaged?: () => void;
  isRemovingManaged?: boolean;
  /** Loop cards only — the same actions the card carries, so opening one does not take them away. */
  onLoopTransition?: ((loopId: string, toState: LoopState) => void) | undefined;
  onLoopRemove?: ((loopId: string) => void) | undefined;
  onLoopConnect?: ((loopId: string) => void) | undefined;
  onLoopDisconnect?: ((loopId: string) => void) | undefined;
  onBack: () => void;
}

/** Single-agent drill-down: meta, the skill file it runs, and its recent runs. */
const AgentDetailView = ({
  card,
  code,
  metricOverride,
  nextOverride,
  liveRuns,
  n8nWorkflowUrl,
  slackAgentUrl = null,
  skillId = null,
  onRemoveManaged,
  isRemovingManaged = false,
  onLoopTransition,
  onLoopRemove,
  onLoopConnect,
  onLoopDisconnect,
  onBack,
}: AgentDetailViewProps) => {
  const detail = FALLBACK;
  const plan = card.plan;
  // A loop wired to a live agent leaves its plan on the card, so only `planned` still means "an idea
  // nobody has run": gating the live half on `plan` cost a wired card its links, runs and skill file.
  const idea = card.planned;
  const kind = agentType(card);
  const launch = plan ? launchLabel(plan) : null;
  const isLiveRuns = !idea && liveRuns.length > 0;
  const rows = idea ? PLANNED_ROWS : isLiveRuns ? liveRuns : detail.rows;
  const metric = metricOverride ?? detail.meta.metric;
  const next = nextOverride ?? detail.meta.next;
  const runMeta = detailRunMeta(card, detail.meta);

  return (
    <section className="view on" id="view-detail">
      <div className="back">
        <button type="button" className="btn btn-ghost" onClick={onBack}>
          ← Back <kbd>Esc</kbd>
        </button>
      </div>

      <div className="panel">
        <div className="panel-b">
          <div className="d-head">
            <span className={`a-dot ${idea ? '' : detail.dot}`} style={{ width: 11, height: 11, margin: 0 }} />
            <div>
              <div className="d-name" data-testid="detail-name">
                {card.name}
              </div>
              <div className="d-sub" data-testid="detail-sub">
                {card.sub}
              </div>
            </div>
            {card.badge ? <span className={`a-badge ${card.badge.className}`}>{card.badge.text}</span> : null}
            {!idea && n8nWorkflowUrl ? (
              <div className="d-right" style={{ display: 'flex', gap: 6 }}>
                <a className="btn btn-primary" href={n8nWorkflowUrl} target="_blank" rel="noreferrer">
                  Open in n8n
                </a>
              </div>
            ) : null}
            {!idea && slackAgentUrl ? (
              <div className="d-right" style={{ display: 'flex', gap: 6 }}>
                <a className="btn btn-primary" href={slackAgentUrl} target="_blank" rel="noreferrer">
                  Talk in Slack
                </a>
              </div>
            ) : null}
            {!idea && onRemoveManaged ? (
              <div className="d-right">
                <button type="button" className="btn btn-ghost" disabled={isRemovingManaged} onClick={onRemoveManaged}>
                  {isRemovingManaged ? 'Removing…' : 'Remove managed card'}
                </button>
              </div>
            ) : null}
          </div>

          <div className="d-obj" data-testid="detail-objective">
            {card.goalParts.map((part, i) =>
              part.bold ? <b key={i}>{part.text}</b> : <span key={i}>{part.text}</span>,
            )}
          </div>

          {card.loop ? (
            <div data-testid="loop-detail">
              <LoopControls
                card={card}
                wired={!card.id.startsWith('loop:')}
                onLoopTransition={onLoopTransition}
                onLoopRemove={onLoopRemove}
                onLoopConnect={onLoopConnect}
                onLoopDisconnect={onLoopDisconnect}
              />
            </div>
          ) : null}

          <div className="a-tags" style={{ marginTop: 10 }}>
            {card.tags.map((t) => (
              <span key={t.label} className={`tg ${t.kind} tone-${runtimeTone(t.kind)}`}>
                {t.label}
              </span>
            ))}
          </div>

          <div className="d-meta">
            {code ? (
              <span>
                code <b className="mono">{code}</b>
              </span>
            ) : null}
            <span>
              type <b>{KIND_LABEL[kind]}</b>
            </span>
            {plan ? (
              <>
                <span>
                  owner <b>{card.owner}</b>
                </span>
                <span>
                  target launch{' '}
                  <b style={launch?.overdue ? { color: 'var(--cc-crit)' } : undefined}>{plan.targetDate || '—'}</b>
                </span>
              </>
            ) : null}
            {idea ? null : (
              <>
                <span data-testid="detail-runs">
                  {runMeta.label} <b>{runMeta.runs}</b>
                </span>
                <span data-testid="detail-succ">
                  success <b style={{ color: SUCC_COLOR[runMeta.succ[1]] }}>{runMeta.succ[0]}</b>
                </span>
                <span>
                  key metric <b className="mono">{metric}</b>
                </span>
                <span>
                  next run <b>{next}</b>
                </span>
              </>
            )}
          </div>

          {plan?.note ? (
            <div className="d-prog">
              note <b>{plan.note}</b>
            </div>
          ) : null}
        </div>
      </div>

      {idea || skillId === null ? null : <SkillFilePanel skillId={skillId} ownerNode={card.owner} />}

      <div className="panel">
        <div className="panel-h">
          <span className="t">Recent runs</span>
          <span className="sub">{isLiveRuns ? 'live' : 'no live runs for this agent yet'}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Started</th>
                <th>Result</th>
                <th>Note</th>
                <th>Took</th>
                <th>Tools</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td>{row[0]}</td>
                  <td className={RESULT[row[1]][0]}>{RESULT[row[1]][1]}</td>
                  <td className="muted-cell">{row[2]}</td>
                  <td className="text-end">{row[3]}</td>
                  <td className="text-end">{row[4]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default AgentDetailView;
